import { useTranslations } from 'next-intl'
import { useCallback } from 'react'

import type { MeterEntityType, MeterType } from '~/app/links/[id]/components/connectedDeviceUtils'
import type { ApplicationStatus } from '~/data/api/hooks/useProfileHistoryApi'

import type { VehicleOptimizationModel } from '../api/client'
import type {
  ChargeEvent,
  ComponentEntityType,
  ConnectedDeviceType,
  ConnectivityIntervalStatus,
  OcppProxyMode,
  OcppProxyOutboundFilter,
  PowerFlow,
  RunState
} from '../api/client/ders'
import { isChargerEventType } from '../types/ems'
import type { NodeLoadManagementOption, OfflineControlsOption, SiteLoadManagementOption } from '../types/site'
import type { BessLiveStatus, BessStatusBucket } from '../utils/bessLiveStatus'
import { isKnownControlMode, normalizeControlMode } from '../utils/controlMode'
import { powerFlowToChargeEvent, powerFlowToGeneratorStatus, powerFlowToSolarArrayStatus } from '../utils/powerFlow'

/**
 * Grid-mode values we have explicit translations for. The `useGridModeFormatter`
 * hook falls back to UNKNOWN for anything outside this set; consumers that need
 * to suppress a UI affordance for unknown values (e.g. don't append a
 * parenthetical when we don't actually have a known mode) can use
 * {@link isKnownDisplayableGridMode}.
 */
const KNOWN_GRID_MODES = ['GRID_FOLLOWING', 'GRID_FORMING', 'UNKNOWN'] as const
type KnownGridMode = (typeof KNOWN_GRID_MODES)[number]
function isKnownGridMode(value: string): value is KnownGridMode {
  return (KNOWN_GRID_MODES as readonly string[]).includes(value)
}

/**
 * `true` when the value is a known grid mode AND has a meaningful display value
 * (i.e. excludes UNKNOWN). Use this to gate UI elements that should only render
 * when we actually know whether the inverter is following or forming.
 */
export function isKnownDisplayableGridMode(value: string | null | undefined): value is 'GRID_FOLLOWING' | 'GRID_FORMING' {
  return value != null && value !== 'UNKNOWN' && isKnownGridMode(value)
}

type Version = 'long' | 'short'

// Function overloads to provide conditional return types
type OfflineControlsFormatter = {
  (offlineControls: OfflineControlsOption, version?: Version): string
  (offlineControls: undefined, version?: Version): undefined
  (offlineControls?: OfflineControlsOption, version?: Version): string | undefined
}

/**
 * Hook that returns a formatter function for offline controls options.
 *
 * @returns A formatter function that takes an offline controls option and version,
 *          returning a localized string representation or undefined if no option provided
 *
 * @example
 * ```tsx
 * const formatOfflineControls = useOfflineControlsFormatter()
 *
 * // Long version (default)
 * formatOfflineControls('ENABLED') // "Enabled for all chargers"
 *
 * // Short version
 * formatOfflineControls('ENABLED', 'short') // "Enabled"
 *
 * // Undefined input
 * formatOfflineControls(undefined) // undefined
 * ```
 */
export function useOfflineControlsFormatter() {
  const t = useTranslations('data.ems')
  return useCallback(
    (offlineControls?: OfflineControlsOption, version: Version = 'long') => {
      return offlineControls ? t(`offlineControls_${offlineControls}_${version}`) : undefined
    },
    [t]
  ) as OfflineControlsFormatter
}

/** Formatter for a Link's OCPP proxy mode. */
export function useOcppProxyModeFormatter() {
  const t = useTranslations('data.ems')
  return useCallback((mode: OcppProxyMode) => t(`ocppProxyMode_${mode}`), [t])
}

/** Formatter for which commands an OCPP proxy upstream may send to chargers. */
export function useOcppOutboundFilterFormatter() {
  const t = useTranslations('data.ems')
  return useCallback((filter: OcppProxyOutboundFilter) => t(`ocppOutboundFilter_${filter}`), [t])
}

/**
 * Hook that returns a formatter function for battery status values.
 *
 * @returns A formatter function that takes a battery status and optional version,
 *          returning a localized string representation
 *
 * @example
 * ```tsx
 * const formatBatteryStatus = useBatteryStatusFormatter()
 *
 * // Status with version support
 * formatBatteryStatus('MAX_CHARGE') // "Maximum charge reached"
 * formatBatteryStatus('MAX_CHARGE', 'short') // "Max charge"
 *
 * // Status without version support
 * formatBatteryStatus('CHARGING') // "Charging"
 * formatBatteryStatus('IDLE') // "Idle"
 * ```
 */
export function useBatteryStatusFormatter() {
  const t = useTranslations('data.ems')

  return useCallback(
    (status: ChargeEvent, version: Version = 'long'): string => {
      switch (status) {
        case 'MAX_CHARGE':
        case 'MIN_CHARGE':
          return t(`batteryStatus_${status}_${version}`)
        default:
          return t(`batteryStatus_${status}`)
      }
    },
    [t]
  )
}

/**
 * Hook that returns a formatter for a resolved battery live status — the flow
 * state plus whatever outranks it.
 *
 * Central so the three surfaces that show this vocabulary (the detail page's
 * Live tile, the overview table's status chip, and the overview roll-up) can't
 * word the same state differently. Degradation reads as "{flow} / Degraded",
 * which keeps the flow rather than replacing it: an impaired battery that is
 * discharging really is discharging.
 */
export function useBessLiveStatusFormatter() {
  const t = useTranslations('data.ems')
  const formatBatteryStatus = useBatteryStatusFormatter()

  return useCallback(
    (status: BessLiveStatus): string => {
      switch (status.kind) {
        case 'offline':
          return t('batteryStatus_OFFLINE')
        case 'faulted':
          return formatBatteryStatus('FAULTED')
        case 'degraded':
          // The flow label is resolved before interpolation, so `t` keeps its
          // literal-key typing rather than being handed a computed key.
          return t('batteryStatus_degradedWithFlow', {
            status: formatBatteryStatus(powerFlowToChargeEvent[status.powerFlow])
          })
        case 'flow':
          return formatBatteryStatus(powerFlowToChargeEvent[status.powerFlow])
        default: {
          const _exhaustive: never = status
          return _exhaustive
        }
      }
    },
    [t, formatBatteryStatus]
  )
}

/**
 * Hook that returns a formatter for one of the aggregate status buckets used
 * by the batteries roll-up. `DEGRADED` carries no flow here — see
 * `BessStatusBucket`.
 */
export function useBessStatusBucketFormatter() {
  const t = useTranslations('data.ems')
  const formatBatteryStatus = useBatteryStatusFormatter()

  return useCallback(
    (bucket: BessStatusBucket): string => {
      switch (bucket) {
        case 'OFFLINE':
          return t('batteryStatus_OFFLINE')
        case 'DEGRADED':
          return t('batteryStatus_DEGRADED')
        case 'FAULTED':
          return formatBatteryStatus('FAULTED')
        case 'IMPORT':
        case 'EXPORT':
        case 'IDLE':
        case 'UNKNOWN':
          return formatBatteryStatus(powerFlowToChargeEvent[bucket])
        default: {
          const _exhaustive: never = bucket
          return _exhaustive
        }
      }
    },
    [t, formatBatteryStatus]
  )
}

/**
 * Hook that returns a formatter for generator status, derived from a `PowerFlow` value.
 *
 * Generators map EXPORT → "Running" (producing power), IMPORT and IDLE → "Idle"
 * (a non-running unit still draws keep-alive power, reported as IMPORT), and
 * anything else (UNKNOWN, undefined) → "Unknown".
 */
export function useGeneratorStatusFormatter() {
  const t = useTranslations('data.ems')
  return useCallback((flow?: PowerFlow): string => t(`generatorStatus_${powerFlowToGeneratorStatus(flow)}`), [t])
}

/**
 * Hook that returns a formatter for solar array status, derived from a `PowerFlow` value.
 *
 * Solar arrays map EXPORT → "Producing", IMPORT and IDLE → "Idle" (a
 * non-producing array's inverter still draws a small tare/keep-alive power,
 * reported as IMPORT), and anything else (UNKNOWN, undefined) → "Unknown".
 */
export function useSolarArrayStatusFormatter() {
  const t = useTranslations('data.ems')
  return useCallback((flow?: PowerFlow): string => t(`solarArrayStatus_${powerFlowToSolarArrayStatus(flow)}`), [t])
}

/**
 * Hook that returns a formatter for `operationalState.runState` — the DER's
 * self-reported health. A snapshot that carries no run state (not every OEM
 * publishes one) reads as "Unknown".
 */
export function useRunStateFormatter() {
  const t = useTranslations('data.ems')
  return useCallback((runState?: RunState | null): string => t(`runState_${runState ?? 'UNKNOWN'}`), [t])
}

/**
 * Hook that returns a formatter for `gridMode` values from a DER's operational
 * state. The API surfaces this as an open string; we map known values to
 * dedicated translations and fall back to UNKNOWN otherwise.
 */
export function useGridModeFormatter() {
  const t = useTranslations('data.ems')
  return useCallback(
    (gridMode?: string | null): string => {
      const resolved = gridMode && isKnownGridMode(gridMode) ? gridMode : 'UNKNOWN'
      return t(`gridMode_${resolved}`)
    },
    [t]
  )
}

/**
 * Hook that returns a formatter for `controlMode` values from a generator's
 * operational state — whether the unit is under automatic, manual, or remote
 * control. The API surfaces this as an open string: known values get dedicated
 * translations, and an unrecognized non-empty string is echoed verbatim so a
 * newly-added backend mode still reads correctly before its translation ships.
 */
export function useControlModeFormatter() {
  const t = useTranslations('data.ems')
  return useCallback(
    (controlMode?: string | null): string => {
      if (isKnownControlMode(controlMode)) return t(`controlMode_${normalizeControlMode(controlMode)}`)
      return controlMode?.trim() || t('controlMode_UNKNOWN')
    },
    [t]
  )
}

/**
 * Hook that returns a formatter for the values a generator's `set_control_mode`
 * command offers, which are what the device advertises rather than a set this app
 * owns. A device can advertise a value no translation exists for yet, so a value
 * with no `mode_*` key is humanised (underscores to spaces, first letter
 * capitalised) rather than shown as a raw key or a missing-translation error.
 *
 * Distinct from {@link useControlModeFormatter}, which labels the mode a device
 * *reports*.
 */
export function useCommandModeFormatter() {
  const t = useTranslations('generators.ControlsTab')
  return useCallback(
    (value: string): string => {
      // Not a key next-intl can see: it is built from a token the device chose,
      // which is what `has` exists to ask about.
      const key = `mode_${value}` as Parameters<typeof t.has>[0]
      if (t.has(key)) return t(key)
      const spaced = value.replaceAll('_', ' ')
      return spaced.charAt(0).toUpperCase() + spaced.slice(1)
    },
    [t]
  )
}

/**
 * Hook that returns a formatter for ``ConnectedDeviceType`` values. The
 * input type is the API enum, so every key is constrained to a real
 * translation at compile time.
 */
export function useDeviceTypeFormatter() {
  const t = useTranslations('data.ems')
  return useCallback((deviceType: ConnectedDeviceType): string => t(`deviceType_${deviceType}`), [t])
}

/**
 * Hook that returns a formatter for ``ConnectivityIntervalStatus`` values
 * (a SynopLink's backend/VPN connectivity state). The input type is the API
 * enum, so every key is constrained to a real translation at compile time.
 */
export function useConnectivityStatusFormatter() {
  const t = useTranslations('data.ems')
  return useCallback((status: ConnectivityIntervalStatus): string => t(`connectivityStatus_${status}`), [t])
}

/**
 * Hook that returns a formatter for meter ``MeterType`` values (single /
 * split / three-phase). The input type is the literal union from
 * `connectedDeviceUtils`, so every key is constrained to a real translation
 * at compile time.
 */
export function useMeterTypeFormatter() {
  const t = useTranslations('synopLinks.ConnectedDevices')
  return useCallback((meterType: MeterType): string => t(`meterType_${meterType}`), [t])
}

/**
 * Hook that returns a formatter for the energy-entity types a meter can be
 * bound to (panel / grid connection / load). The input type is the literal
 * union from `connectedDeviceUtils`, so every key is constrained to a real
 * translation at compile time.
 */
export function useMeterEntityTypeFormatter() {
  const t = useTranslations('synopLinks.ConnectedDevices')
  return useCallback((entityType: MeterEntityType): string => t(`energyEntityType_${entityType}`), [t])
}

/**
 * Hook that returns a formatter for the kinds of asset an energy system
 * component can stand for. The input type is the API enum, so every key is
 * constrained to a real translation at compile time — and the coarse family
 * vocabulary the archetype registry speaks cannot be passed by mistake.
 */
export function useComponentEntityTypeFormatter() {
  const t = useTranslations('sites.details')
  return useCallback(
    (entityType: ComponentEntityType): string => t(`EnergySystemsTab_entityType_${entityType}`),
    [t]
  )
}

/**
 * Hook that returns a formatter for a device's `modelType` token.
 *
 * The input is a plain string rather than a literal union, because the
 * vocabulary is served by ders-api now and the frontend no longer holds a list
 * to constrain it against. That trades a compile-time guarantee for a runtime
 * one: a token registered in ders-api before anyone adds a translation here
 * falls back to the token itself rather than throwing, so onboarding an OEM
 * cannot break the form. The token is a readable name already (`deepsea`,
 * `aggreko`), so the fallback is legible rather than a raw key.
 */
export function useModelTypeFormatter() {
  const t = useTranslations('synopLinks.ConnectedDevices')
  return useCallback(
    (modelType: string): string => {
      // next-intl types its keys off the message catalogue, so a key built from
      // a runtime token is not one of them. The cast is the honest shape of
      // what this does: ask about a key the catalogue may not have, which is
      // exactly what `has` exists for.
      const key = `modelType_${modelType}` as Parameters<typeof t.has>[0]
      return t.has(key) ? t(key) : modelType
    },
    [t]
  )
}

/**
 * Hook that returns a formatter for a model's `regulationType` value.
 *
 * Plain string for the same reason as the model type: ders-api serves the
 * values, so one registered there before it is translated here falls back to
 * the value itself rather than throwing.
 */
export function useRegulationTypeFormatter() {
  const t = useTranslations('synopLinks.ConnectedDevices')
  return useCallback(
    (regulationType: string): string => {
      const key = `regulationType_${regulationType}` as Parameters<typeof t.has>[0]
      return t.has(key) ? t(key) : regulationType
    },
    [t]
  )
}

/**
 * Hook that returns a formatter function for profile application status values.
 *
 * @returns A formatter function that takes an optional profile application status,
 *          returning a localized string representation (defaults to 'UNKNOWN' if undefined)
 *
 * @example
 * ```tsx
 * const formatProfileStatus = useProfileApplicationStatusFormatter()
 *
 * // Valid status
 * formatProfileStatus('APPLIED') // "Applied"
 * formatProfileStatus('PENDING') // "Pending"
 *
 * // Undefined status
 * formatProfileStatus(undefined) // "Unknown"
 * formatProfileStatus() // "Unknown"
 * ```
 */
export function useProfileApplicationStatusFormatter() {
  const t = useTranslations('data.ems')
  return useCallback((status?: ApplicationStatus) => t(`profileApplicationStatus_${status ?? 'FAILED'}`), [t])
}

/**
 * Hook that returns a formatter function for charger event type values.
 *
 * @returns A formatter function that takes an optional event type string,
 *          returning a localized string representation (validates input and defaults to 'UNKNOWN' for invalid types)
 *
 * @example
 * ```tsx
 * const formatChargerEventType = useChargerEventTypeFormatter()
 *
 * // Valid event types
 * formatChargerEventType('CHARGING_START') // "Charging started"
 * formatChargerEventType('CHARGING_END') // "Charging ended"
 *
 * // Invalid or undefined event types
 * formatChargerEventType('INVALID_TYPE') // "Unknown"
 * formatChargerEventType(undefined) // "Unknown"
 * formatChargerEventType() // "Unknown"
 * ```
 */
export function useChargerEventTypeFormatter() {
  const t = useTranslations('data.ems')
  return useCallback((eventType?: string) => t(`chargerEventType_${isChargerEventType(eventType) ? eventType : 'UNKNOWN'}`), [t])
}

/**
 * Hook that provides a formatLoadManagement function with translations
 */
export function useLoadManagementFormatter() {
  const t = useTranslations('data.ems')

  return useCallback(
    (loadManagement?: SiteLoadManagementOption | NodeLoadManagementOption) => t(`loadManagement_${loadManagement ?? 'OFF'}`),
    [t]
  )
}

/**
 * Hook that returns a formatter for a vehicle optimization's infeasibility reason — why a charging
 * plan cannot reach its target (e.g. `SITE_POWER_LIMIT` → "Site Limit Constrained"). A missing
 * reason formats as OTHER.
 */
export function useInfeasibilityReasonFormatter() {
  const t = useTranslations('data.ems')
  return useCallback(
    (reason?: VehicleOptimizationModel['infeasibilityReason']): string => t(`infeasibilityReason_${reason ?? 'OTHER'}`),
    [t]
  )
}
